import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal,
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { Button } from 'primeng/button';
import { Card } from 'primeng/card';
import { Paginator, PaginatorState } from 'primeng/paginator';
import { TableModule } from 'primeng/table';
import { Tooltip } from 'primeng/tooltip';
import { ControlErrorDirective } from '../../../../../shared/directive/control-error.directive';
import { AppIconComponent } from '../../../../../shared/components/app-icon/app-icon.component';
import { AnnouncementRecipient } from '../../../../../shared/models/announcement-recipient';

@Component({
  selector: 'app-announcement-recipients',
  imports: [
    AppIconComponent,
    Button,
    Card,
    ControlErrorDirective,
    DatePipe,
    Paginator,
    TableModule,
    Tooltip,
    ControlErrorDirective,
  ],
  templateUrl: './announcement-recipients.component.html',
  styleUrl: './announcement-recipients.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnnouncementRecipientsComponent {
  readonly recipients = input.required<AnnouncementRecipient[]>();
  readonly control = input.required<AbstractControl>();
  readonly addRequested = output<void>();
  readonly removeRequested = output<AnnouncementRecipient>();

  protected readonly recipientPageIndex = signal(0);
  protected readonly recipientPageSize = signal(5);
  protected readonly recipientSortField =
    signal<keyof AnnouncementRecipient>('lastName');
  protected readonly recipientSortOrder = signal<1 | -1>(1);

  protected readonly sortedRecipients = computed(() => {
    const recipients = [...this.recipients()];
    const field = this.recipientSortField();
    const order = this.recipientSortOrder();

    return recipients.sort((left, right) => {
      const leftValue = left[field];
      const rightValue = right[field];

      if (leftValue == null && rightValue == null) return 0;
      if (leftValue == null) return -1 * order;
      if (rightValue == null) return 1 * order;

      const normalizedLeft = this.normalizeSortValue(leftValue);
      const normalizedRight = this.normalizeSortValue(rightValue);

      if (
        typeof normalizedLeft === 'number' &&
        typeof normalizedRight === 'number'
      ) {
        return (normalizedLeft - normalizedRight) * order;
      }

      return (
        String(normalizedLeft).localeCompare(String(normalizedRight), 'pl') *
        order
      );
    });
  });

  protected readonly visibleRecipients = computed(() => {
    const pageIndex = this.recipientPageIndex();
    const pageSize = this.recipientPageSize();
    const start = pageIndex * pageSize;

    return this.sortedRecipients().slice(start, start + pageSize);
  });

  constructor() {
    effect(() => {
      const totalRecipients = this.recipients().length;
      const pageSize = this.recipientPageSize();
      const maxPageIndex = Math.max(
        Math.ceil(totalRecipients / pageSize) - 1,
        0,
      );

      if (this.recipientPageIndex() > maxPageIndex) {
        this.recipientPageIndex.set(maxPageIndex);
      }
    });
  }

  protected onAddRequested(): void {
    this.addRequested.emit();
  }

  protected onRemoveRequested(recipient: AnnouncementRecipient): void {
    this.removeRequested.emit(recipient);
  }

  protected onRecipientsPageChange(event: PaginatorState): void {
    this.recipientPageIndex.set((event.first || 0) / (event.rows || 5));
    this.recipientPageSize.set(event.rows || 5);
  }

  protected onRecipientsSort(event: {
    field?: keyof AnnouncementRecipient;
    order?: 1 | -1;
  }): void {
    if (!event.field || !event.order) {
      return;
    }

    this.recipientSortField.set(event.field);
    this.recipientSortOrder.set(event.order);
  }

  private normalizeSortValue(
    value: AnnouncementRecipient[keyof AnnouncementRecipient],
  ): string | number | boolean {
    if (value instanceof Date) {
      return value.getTime();
    }

    return value;
  }
}
